import { useCallback, useEffect, useRef, useState } from 'react';
import { VISIT_END_TEXT, type VisitsReply } from '@london-life/contracts';
import { describeError } from '@london-life/errors';
import { api } from '../lib/api';
import { notify, notifyProblem } from '../lib/alerts';
import type { Realtime } from '../chat/use-realtime';

// Visits as the app sees them: the one you are on (as a guest) and who is at your door or inside (as a host).
// Looked at again on every "visit" event, on the realtime line coming back, every 15 seconds while something is
// going on (a knock waiting for its answer ends by itself on the server), and once a minute otherwise (a knock
// still reaches a host whose live line is down).
export function useVisits(enabled: boolean, realtime: Realtime, onMoved: () => void) {
  const [reply, setReply] = useState<VisitsReply | null>(null);
  const [busy, setBusy] = useState(false);
  // a visit that ended is said once
  const told = useRef<string | null>(null);
  const moved = useRef(onMoved);
  moved.current = onMoved;
  const accept = useCallback((next: VisitsReply) => {
    setReply(next);
    const visit = next.visit;
    if (visit?.ended && told.current !== visit.id) {
      told.current = visit.id;
      const host = visit.host.username ? `@${visit.host.username}` : visit.host.firstName;
      // the guest's own leaving needs no words
      if (visit.ended !== 'left') notify(VISIT_END_TEXT[visit.ended](host));
      moved.current();
    }
  }, []);
  const load = useCallback(async () => {
    if (!enabled) return setReply(null);
    try {
      accept(await api.visits.get());
    } catch {
      // the next look tries again
    }
  }, [enabled, accept]);
  useEffect(() => {
    void load();
  }, [load, realtime.generation]);
  useEffect(() => {
    if (!enabled) return;
    return realtime.listen((event) => {
      if (event.type === 'visit') {
        void load();
        moved.current();
      }
    });
  }, [enabled, realtime, load]);
  const going = Boolean(reply?.visit && reply.visit.status !== 'ended') || Boolean(reply?.guests.length);
  useEffect(() => {
    if (!enabled) return;
    const timer = window.setInterval(() => void load(), going ? 15_000 : 60_000);
    return () => window.clearInterval(timer);
  }, [enabled, going, load]);
  const run = useCallback(
    async (work: () => Promise<VisitsReply>) => {
      setBusy(true);
      try {
        const next = await work();
        accept(next);
        moved.current();
        return next;
      } catch (error) {
        notifyProblem(describeError(error).message);
        return null;
      } finally {
        setBusy(false);
      }
    },
    [accept],
  );
  return {
    reply,
    busy,
    reload: load,
    start: (hostLifeId: string, mode: Parameters<typeof api.visits.start>[0]['mode']) =>
      run(() => api.visits.start({ hostLifeId, mode }, `visit-${crypto.randomUUID()}`)),
    leave: () => run(() => api.visits.leave()),
    answer: (visitId: string, letIn: boolean) => run(() => api.visits.answer(visitId, letIn)),
    sendHome: (visitId: string) => run(() => api.visits.sendHome(visitId)),
  };
}
export type Visits = ReturnType<typeof useVisits>;
